import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { COUNTRY_DIVISIONS, getCountry } from '../services/country';
import { useAuth } from '../context/AuthContext';
import JobCard from '../components/jobs/JobCard';
import PageSeo from '../components/seo/PageSeo';
import styles from './Jobs.module.css';

const MODALITIES = [{v:'',l:'Todas'},{v:'presential',l:'Presencial'},{v:'remote',l:'Remoto'},{v:'hybrid',l:'Híbrido'}];
const TYPES = [{v:'',l:'Todos'},{v:'full_time',l:'Tiempo completo'},{v:'part_time',l:'Media jornada'},{v:'internship',l:'Prácticas'},{v:'freelance',l:'Freelance'}];
const EXP_LEVELS = [{v:'',l:'Cualquier nivel'},{v:'no_experience',l:'Sin experiencia'},{v:'junior',l:'Junior'},{v:'mid',l:'Intermedio'},{v:'senior',l:'Senior'},{v:'lead',l:'Lead/Manager'}];

export default function Jobs() {
  const country=getCountry();
  const pt=country.code==='BR';
  const modalityOptions=pt?[{v:'',l:'Todas'},{v:'presential',l:'Presencial'},{v:'remote',l:'Remoto'},{v:'hybrid',l:'Híbrido'}]:MODALITIES;
  const typeOptions=pt?[{v:'',l:'Todos'},{v:'full_time',l:'Tempo integral'},{v:'part_time',l:'Meio período'},{v:'internship',l:'Estágio'},{v:'freelance',l:'Freelance'}]:TYPES;
  const experienceOptions=pt?[{v:'',l:'Qualquer nível'},{v:'no_experience',l:'Sem experiência'},{v:'junior',l:'Júnior'},{v:'mid',l:'Pleno'},{v:'senior',l:'Sênior'},{v:'lead',l:'Liderança/Gestão'}]:EXP_LEVELS;
  const formatMoney=value=>new Intl.NumberFormat(country.locale,{style:'currency',currency:country.currency,maximumFractionDigits:0}).format(value);
  const seoTitle=pt?'Vagas de emprego no Brasil | TalentosRD':`Empleos y trabajos en ${country.name} | TalentosRD`;
  const seoDescription=pt?'Pesquise vagas de emprego no Brasil por cargo, estado, categoria e modalidade de trabalho.':`Busca empleos y trabajos en ${country.name} por cargo, ${country.divisionSingular}, categoría y modalidad.`;
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [smartSearch, setSmartSearch] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    modality: searchParams.get('modality') || '',
    job_type: searchParams.get('job_type') || '',
    experience_level: '',
    province: searchParams.get('province') || searchParams.get('city') || '',
    salary_min: '',
    is_featured: searchParams.get('is_featured') || '',
    sort: 'newest',
    page: 1,
    limit: 15,
  });

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data));
    if (user?.role === 'candidate') {
      api.get('/saved-jobs').then(r => setSavedIds(new Set(r.data.map(j=>j.id))));
    }
  }, [user]);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([,v])=>v&&v!==''));
    if (smartSearch && filters.search.trim()) params.smart = 'true';
    const { data } = await api.get('/jobs', { params });
    setJobs(data.jobs);
    setTotal(data.total);
    setPages(data.pages);
    setLoading(false);
  }, [filters, smartSearch]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const set = (k, v) => setFilters(p => ({ ...p, [k]: v, page: 1 }));
  const clear = () => setFilters(p => ({ search:'', category:'', modality:'', job_type:'', experience_level:'', province:'', salary_min:'', is_featured:'', sort:'newest', page:1, limit:p.limit }));

  const toggleSave = async (jobId, isSaved) => {
    if (!user) return;
    if (isSaved) {
      await api.delete(`/saved-jobs/${jobId}`);
      setSavedIds(p => { const n=new Set(p); n.delete(jobId); return n; });
    } else {
      await api.post(`/saved-jobs/${jobId}`);
      setSavedIds(p => new Set([...p, jobId]));
    }
  };

  const activeFilters = [filters.category, filters.modality, filters.job_type, filters.experience_level, filters.province, filters.salary_min].filter(Boolean).length;

  return (
    <div className={styles.page}>
      <PageSeo title={seoTitle} description={seoDescription} path="/jobs" alternates/>
      {/* Top bar */}
      <div className={styles.topBar}>
        <div className="container">
          <div className={styles.topBarInner}>
            <div className={styles.searchBar}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input value={filters.search} onChange={e=>set('search',e.target.value)} placeholder={pt?'Cargo, empresa ou habilidade...':'Cargo, empresa o habilidad...'} onKeyDown={e=>e.key==='Enter'&&fetchJobs()} />
            </div>
            <div className={styles.searchBar} style={{maxWidth:220}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <input value={filters.province} onChange={e=>set('province',e.target.value)} placeholder={`${country.divisionSingular}...`} list="prov-list"/>
              <datalist id="prov-list">{COUNTRY_DIVISIONS.map(p=><option key={p} value={p}/>)}</datalist>
            </div>
            <button className="btn btn-primary" onClick={fetchJobs}>{pt?'Buscar':'Buscar'}</button>
            <button className={`${styles.smartButton} ${smartSearch ? styles.smartActive : ''}`} onClick={()=>setSmartSearch(v=>!v)} aria-pressed={smartSearch} title={pt?'Busca também cargos e habilidades relacionados':'Busca también cargos y habilidades relacionados'}>
              ✨ <span>IA</span>
            </button>
          </div>
        </div>
      </div>

      <div className={`container ${styles.layout}`}>
        {/* Sidebar */}
        <aside className={`${styles.sidebar} ${filtersOpen ? styles.sidebarOpen : ''}`}>
          <div className={styles.sidebarHead}>
            <h3>{pt?'Filtros':'Filtros'} {activeFilters > 0 && <span className={styles.filterCount}>{activeFilters}</span>}</h3>
            {activeFilters > 0 && <button onClick={clear} className={styles.clearBtn}>{pt?'Limpar':'Limpiar'}</button>}
          </div>

          <div className={styles.filterSection}>
            <h4>{pt?'Categoria':'Categoría'}</h4>
            <div className={styles.catList}>
              <button className={!filters.category?styles.catActive:''} onClick={()=>set('category','')}>{pt?'Todas':'Todas'}</button>
              {categories.map(c=>(
                <button key={c.id} className={filters.category===c.slug?styles.catActive:''} onClick={()=>set('category',c.slug)}>
                  {c.name} <span>{c.job_count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className={styles.filterSection}>
            <h4>{pt?'Modalidade':'Modalidad'}</h4>
            {modalityOptions.map(m=>(
              <label key={m.v} className={styles.radio}>
                <input type="radio" name="modality" checked={filters.modality===m.v} onChange={()=>set('modality',m.v)}/>{m.l}
              </label>
            ))}
          </div>

          <div className={styles.filterSection}>
            <h4>{pt?'Tipo de contrato':'Tipo de jornada'}</h4>
            {typeOptions.map(t=>(
              <label key={t.v} className={styles.radio}>
                <input type="radio" name="job_type" checked={filters.job_type===t.v} onChange={()=>set('job_type',t.v)}/>{t.l}
              </label>
            ))}
          </div>

          <div className={styles.filterSection}>
            <h4>{pt?'Experiência necessária':'Experiencia requerida'}</h4>
            {experienceOptions.map(e=>(
              <label key={e.v} className={styles.radio}>
                <input type="radio" name="exp" checked={filters.experience_level===e.v} onChange={()=>set('experience_level',e.v)}/>{e.l}
              </label>
            ))}
          </div>

          <div className={styles.filterSection}>
            <h4>{pt?'Salário mínimo':'Salario mínimo'} ({country.currency})</h4>
            <select value={filters.salary_min} onChange={e=>set('salary_min',e.target.value)} className={styles.select}>
              <option value="">{pt?'Sem mínimo':'Sin mínimo'}</option>
              {[15000,25000,40000,60000,80000,100000].map(value=><option key={value} value={value}>{formatMoney(value)}+</option>)}
            </select>
          </div>

          <div className={styles.filterSection}>
            <label className={styles.checkLabel}>
              <input type="checkbox" checked={filters.is_featured==='true'} onChange={e=>set('is_featured',e.target.checked?'true':'')}/>
              {pt?'Somente vagas em destaque':'Solo empleos destacados'} ⭐
            </label>
          </div>
        </aside>

        {/* Main content */}
        <main className={styles.main}>
          <div className={styles.resultsBar}>
            <div className={styles.resultsTitle}>
              <h1>{pt?'Vagas de emprego no Brasil':`Empleos en ${country.name}`}</h1>
              <p><strong>{total.toLocaleString(country.locale)}</strong> {pt?'vagas encontradas':'empleos encontrados'}
              {filters.search && <span> {pt?'para':'para'} "<em>{filters.search}</em>"</span>}
              </p>
            </div>
            <div className={styles.sortControls}>
              <button className={styles.mobileFilter} onClick={()=>setFiltersOpen(o=>!o)}>
                🔧 {pt?'Filtros':'Filtros'} {activeFilters>0&&`(${activeFilters})`}
              </button>
              <select value={filters.sort} onChange={e=>set('sort',e.target.value)} className={styles.sortSelect}>
                <option value="newest">{pt?'Mais recentes':'Más recientes'}</option>
                <option value="relevance">{pt?'Relevância':'Relevancia'}</option>
                <option value="salary_desc">{pt?'Maior salário':'Mayor salario'}</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="page-loading"><div className="spinner"/><span>{pt?'Buscando vagas...':'Buscando empleos...'}</span></div>
          ) : jobs.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>🔍</div>
              <h3>{pt?'Não encontramos vagas com esses filtros':'No encontramos ofertas con esos filtros'}</h3>
              <p>{pt?'Tente alterar os critérios de busca ou remover alguns filtros.':'Intenta cambiar los criterios de búsqueda o elimina algunos filtros.'}</p>
              <button onClick={clear} className="btn btn-outline">{pt?'Limpar filtros':'Limpiar filtros'}</button>
            </div>
          ) : (
            <>
              <div className={styles.jobsList}>
                {jobs.map(job => (
                  <JobCard key={job.id} job={job} saved={savedIds.has(job.id)}
                    onSave={user?.role==='candidate' ? toggleSave : null} />
                ))}
              </div>
              {pages > 1 && (
                <div className={styles.pagination}>
                  <button disabled={filters.page===1} onClick={()=>set('page',filters.page-1)} className="btn btn-ghost btn-sm">← {pt?'Anterior':'Anterior'}</button>
                  <div className={styles.pages}>
                    {Array.from({length:Math.min(pages,7)},(_,i)=>{
                      let p = i+1;
                      if(pages>7){
                        if(filters.page<=4) p=i+1;
                        else if(filters.page>=pages-3) p=pages-6+i;
                        else p=filters.page-3+i;
                      }
                      return <button key={p} className={p===filters.page?styles.activePage:''} onClick={()=>set('page',p)}>{p}</button>;
                    })}
                  </div>
                  <button disabled={filters.page===pages} onClick={()=>set('page',filters.page+1)} className="btn btn-ghost btn-sm">{pt?'Próxima':'Siguiente'} →</button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
